import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import authService from '../../services/authService';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = sessionStorage.getItem('resetEmail');
    if (savedEmail) setEmail(savedEmail);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (otp.length !== 6) {
      setError('OTP must be 6 digits');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(
        email,
        otp,
        newPassword,
        confirmPassword
      );

      setMessage(response.message || 'Password reset successfully');

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loader when resetting password
  if (loading) {
    return <Loader fullScreen={true} size="default" message="Resetting your password..." />
  }

  return (
    <div className="min-h-screen md:h-screen relative md:overflow-hidden bg-transparent">

      {/* ===== BACKGROUND IMAGE (tablet & desktop only) ===== */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage: "url('/images/login-bg-gradient.png')",
        }}
      />

      {/* ===== CONTENT WRAPPER ===== */}
      <div className="relative z-10 min-h-screen md:h-full flex items-center justify-center md:justify-start py-10 md:py-0">
        <div className="container mx-auto px-4 md:px-6 w-full">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

            {/* ================= LEFT CONTENT ================= */}
            <div className="block">

              <h1 className="text-4xl font-bold tracking-tight">
                <img
                  src="/images/DQ-logix-image.png"
                  alt="DQ Logix Logistics" 
                  className="w-[50%]"
                />
              </h1>

              <p className="text-lg uppercase tracking-widest text-gray-500 mt-6">
                From shipment to settlement,
              </p>

              <p className="text-3xl font-semibold text-[#3467ae] uppercase mt-2">
                We support your entire journey.
              </p>

              {/* ILLUSTRATION */}
              <img
                src="/images/dq-logix-login-illustration.png"
                alt="DQ Logix Logistics"
                className="w-full max-w-4xl drop-shadow-xl mt-10 hidden md:block"
              />

              {/* ===== FOOTER NOTE ===== */}
              <p className="text-sm text-gray-400 text-left mt-8 hidden md:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
              </p>
            </div>

            {/* ================= RIGHT RESET PASSWORD CARD ================= */}
            <div className="flex justify-center md:justify-end w-full">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6">

                {/* ===== RESET PASSWORD SECTION ===== */}
                <div>
                  <h2 className="text-xl font-semibold mb-2">
                    Reset Password
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Enter OTP and choose a new password
                  </p>

                  {/* ERROR MESSAGE */}
                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {/* SUCCESS MESSAGE */}
                  {message && (
                    <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                      {message}
                      <p className="text-xs mt-1 text-green-600">
                        Redirecting to login...
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* EMAIL INPUT */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none"
                        required
                        disabled={loading}
                      />
                    </div>

                    {/* OTP INPUT */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OTP Code
                      </label>
                      <input
                        type="text"
                        placeholder="000000"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none text-center tracking-widest font-semibold"
                        required
                        disabled={loading}
                        maxLength={6}
                      />
                    </div>

                    {/* NEW PASSWORD INPUT WITH EYE ICON */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none pr-12"
                          required
                          disabled={loading}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={showNewPassword ? EyeOff : Eye}
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 !p-1 !border-0"
                        />
                      </div>
                    </div>

                    {/* CONFIRM PASSWORD INPUT WITH EYE ICON */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 outline-none pr-12"
                          required
                          disabled={loading}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={showConfirmPassword ? EyeOff : Eye}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 !p-1 !border-0"
                        />
                      </div>
                    </div>

                    {/* RESET PASSWORD BUTTON */}
                    <Button
                      variant="primary"
                      type="submit"
                      fullWidth
                      loading={loading}
                      size="lg"
                      className="py-3 rounded-lg"
                    >
                      {loading ? 'RESETTING PASSWORD...' : 'RESET PASSWORD'}
                    </Button>

                    {/* BACK TO LOGIN */}
                    <div className="flex flex-col items-center gap-2 pt-4">
                      <Link
                        to="/login"
                        className="inline-flex items-center justify-center w-full py-2.5 px-4 rounded-lg border border-[#3467ae] text-sm font-medium text-[#3467ae] hover:bg-[#3467ae]/5 transition"
                      >
                        ← Back to Login
                      </Link>
                    </div>

                    <p className="text-xs text-gray-500 text-center pt-2">
                      By continuing, you accept our Terms & Conditions
                      and Privacy Policy.
                    </p>
                  </form>
                </div>

              </div>
            </div>

            <p className="text-sm text-gray-400 text-left mt-0 md:hidden sm:block">
                All Rights Reserved. Designed with passion by{" "}
                <span className="font-semibold text-[#3467ae]">DIGIQIN</span>
            </p>

          </div>

        </div>
      </div>
    </div>
  );
};

export default ResetPassword;